/**
 * Accounting > Authorize.net Deposits (Mercedes, Oct 29, 2026).
 *
 * Each day's settled Authorize.net batch, prepared as the invoice and next-day payment
 * Mercedes used to key by hand: one invoice to "Authorize.net" with a line per card type
 * (charges less refunds) on its Credit Card Sales account, a payment for the full amount the
 * next day, and a settlement summary PDF attached to both. Nothing goes to QuickBooks until
 * Greg or Mercedes approves the day here.
 *
 * Limited to Greg and Mercedes on the server (authnet-qbo.js). The server re-checks
 * everything at approval; this screen only shows what it will do.
 *
 * Post selected (Mercedes, Nov 3, 2026): tick several Ready days (or days whose invoice is in
 * and payment is not) and post them in one go. Each day is still its own approve call, the
 * same one the single-day button makes, oldest first; a day that fails is skipped and the rest
 * carry on, then a summary lists what posted and what did not.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronRight, FileText, RefreshCw, AlertTriangle } from 'lucide-react';
import { authnetQbo } from '../services/api';

const money = (v) => {
  if (v == null || !isFinite(Number(v))) return '';
  const n = Number(v);
  const s = '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `(${s})` : s;
};
const day = (d, withYear = true) => {
  if (!d) return '';
  const [y, m, dd] = String(d).slice(0, 10).split('-').map(Number);
  if (!y || !m || !dd) return '';
  return new Date(y, m - 1, dd).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}) });
};
const CARD_COLS = [
  { type: 'Visa', label: 'Visa' },
  { type: 'MasterCard', label: 'MasterCard' },
  { type: 'AmericanExpress', label: 'Amex' },
  { type: 'Discover', label: 'Discover' },
];

const box = { border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 8, background: 'var(--white, #fff)' };
const small = { fontSize: 12, color: 'var(--gray-500, #6b7280)' };
const cell = { padding: '7px 8px', fontSize: 13, textAlign: 'left', verticalAlign: 'top', borderTop: '1px solid var(--gray-100, #f3f4f6)' };
const num = { ...cell, textAlign: 'right', whiteSpace: 'nowrap' };
const head = { padding: '6px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--gray-500, #6b7280)', textAlign: 'left' };

function chipFor(d) {
  const st = d.stored && d.stored.status;
  if (st === 'posted') return { text: `Posted #${d.stored.invoiceDocNumber || d.stored.invoiceId}`, bg: '#dcfce7', fg: '#166534' };
  if (st === 'partial') return { text: 'Invoice in, payment not', bg: '#fee2e2', fg: '#991b1b' };
  if (st === 'posting') return { text: 'Posting...', bg: '#e0e7ff', fg: '#3730a3' };
  if (d.inBooks && d.inBooks.length) return { text: `In QuickBooks #${d.inBooks.map((x) => x.docNumber || x.id).join(', #')}`, bg: '#f3f4f6', fg: '#374151' };
  if (!d.batchIds.length) return { text: 'No batch settled', bg: '#fef3c7', fg: '#92400e' };
  if (d.checking) return { text: 'Checking...', bg: '#f3f4f6', fg: '#6b7280' };
  if (st === 'failed') return { text: 'Failed, see note', bg: '#fee2e2', fg: '#991b1b' };
  if (!d.day) return { text: 'Not checked', bg: '#f3f4f6', fg: '#6b7280' };
  if (d.day.blockers && d.day.blockers.length) return { text: 'Needs attention', bg: '#fee2e2', fg: '#991b1b' };
  if (d.existing && (d.existing.liveInvoices.length || d.existing.mirrorInvoices.length)) return { text: 'In QuickBooks', bg: '#f3f4f6', fg: '#374151' };
  if (d.day.waiting) return { text: `Deposit ${day(d.paymentDate, false)}`, bg: '#fef3c7', fg: '#92400e' };
  return { text: 'Ready', bg: '#dbeafe', fg: '#1e40af' };
}

function canApprove(d) {
  const st = d.stored && d.stored.status;
  if (st === 'posted' || st === 'posting') return false;
  if (st === 'partial') return true;
  if (!d.day || d.checking) return false;
  if (d.inBooks && d.inBooks.length) return false;
  if (d.existing && (d.existing.liveInvoices.length || d.existing.mirrorInvoices.length)) return false;
  return !(d.day.blockers && d.day.blockers.length) && !d.day.waiting;
}

function Chip({ c }) {
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>{c.text}</span>;
}

function DayDetail({ d, onPdf, onApprove, busy }) {
  const x = d.day;
  const st = d.stored || {};
  return (
    <div style={{ padding: '10px 14px 14px', background: 'var(--gray-50, #f9fafb)' }}>
      {st.error && <div style={{ color: '#991b1b', fontSize: 13, marginBottom: 8 }}><AlertTriangle size={13} /> {st.error}</div>}
      {x && x.blockers && x.blockers.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#991b1b' }}>Needs attention</div>
          <ul style={{ margin: '4px 0 0 18px', fontSize: 13 }}>{x.blockers.map((b) => <li key={b}>{b}</li>)}</ul>
        </div>
      )}
      {x && x.notes && x.notes.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Notes</div>
          <ul style={{ margin: '4px 0 0 18px', fontSize: 13 }}>{x.notes.map((b) => <li key={b}>{b}</li>)}</ul>
        </div>
      )}
      {x && x.lines && x.lines.length > 0 && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ ...box, padding: 10, flex: '1 1 380px' }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
              Invoice {st.invoiceDocNumber ? `#${st.invoiceDocNumber}` : '(number given when posted)'} dated {day(x.date)}, customer Authorize.net
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {x.lines.map((l) => (
                  <tr key={l.card}>
                    <td style={cell}>{l.description}</td>
                    <td style={{ ...cell, ...small }}>{l.accountName}</td>
                    <td style={num}>{money(l.amount)}</td>
                  </tr>
                ))}
                <tr><td style={{ ...cell, fontWeight: 600 }}>Total</td><td style={cell} /><td style={{ ...num, fontWeight: 600 }}>{money(x.total)}</td></tr>
              </tbody>
            </table>
          </div>
          <div style={{ ...box, padding: 10, flex: '1 1 260px' }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Payment dated {day(x.paymentDate)}</div>
            <div style={{ fontSize: 13 }}>{money(x.total)} against the invoice, deposited to {(x.payment && (x.payment.depositAccountName || x.payment.depositAccountId)) || 'the usual account'}.</div>
            <div style={{ ...small, marginTop: 6 }}>Batch {x.batchIds.join(', ')}. The settlement summary PDF is attached to both. Merchant fees are not included.</div>
          </div>
        </div>
      )}
      <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {d.batchIds.length > 0 && (
          <button type="button" className="btn btn-ghost" onClick={() => onPdf(d)} disabled={busy}><FileText size={14} /> Settlement summary PDF</button>
        )}
        {canApprove(d) && (
          <button type="button" className="btn btn-primary" onClick={() => onApprove(d)} disabled={busy}>
            <Check size={14} /> {st.status === 'partial' ? 'Post the payment' : 'Approve and post'}
          </button>
        )}
      </div>
    </div>
  );
}

function ConfirmDialog({ d, onCancel, onConfirm, busy, err }) {
  const x = d.day;
  const partial = d.stored && d.stored.status === 'partial';
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ ...box, padding: 20, maxWidth: 560, width: '100%' }}>
        <h3 style={{ margin: '0 0 6px', color: 'var(--navy, #003b5c)' }}>{partial ? 'Post the payment' : 'Approve and post'} {day(x.date)}</h3>
        <div style={{ fontSize: 13, marginBottom: 10 }}>
          {partial
            ? <>Invoice #{d.stored.invoiceDocNumber || d.stored.invoiceId} is already in QuickBooks. This receives its payment of <b>{money(x.total)}</b> dated {day(x.paymentDate)}.</>
            : <>This creates an invoice to Authorize.net dated {day(x.date)} for <b>{money(x.total)}</b> and a payment for the same amount dated {day(x.paymentDate)}, with the settlement summary attached to each. CPx checks the batch and QuickBooks again before anything is sent.</>}
        </div>
        {!partial && (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
            <tbody>
              {x.lines.map((l) => (
                <tr key={l.card}><td style={cell}>{l.description}</td><td style={{ ...cell, ...small }}>{l.accountName}</td><td style={num}>{money(l.amount)}</td></tr>
              ))}
            </tbody>
          </table>
        )}
        {x.notes && x.notes.length > 0 && <ul style={{ margin: '0 0 10px 18px', fontSize: 12 }}>{x.notes.map((n) => <li key={n}>{n}</li>)}</ul>}
        {err && <div style={{ color: '#991b1b', fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={onConfirm} disabled={busy}>{busy ? 'Posting...' : (partial ? 'Post the payment' : 'Post to QuickBooks')}</button>
        </div>
      </div>
    </div>
  );
}

function BatchConfirm({ list, onCancel, onConfirm }) {
  const total = list.reduce((s, d) => s + Number(d.day.total || 0), 0);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ ...box, padding: 20, maxWidth: 560, width: '100%' }}>
        <h3 style={{ margin: '0 0 6px', color: 'var(--navy, #003b5c)' }}>Post {list.length} day{list.length === 1 ? '' : 's'}, total {money(total)}?</h3>
        <div style={{ fontSize: 13, marginBottom: 10 }}>
          Oldest first, one at a time. Each day gets the same checks as posting it on its own: Authorize.net and QuickBooks are read again,
          the day has to tie out and still total what you see here, and nothing already in QuickBooks is posted twice. A day that fails is skipped and the rest carry on.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
          <tbody>
            {list.map((d) => (
              <tr key={d.date}>
                <td style={cell}>{day(d.date)}</td>
                <td style={{ ...cell, ...small }}>{d.stored && d.stored.status === 'partial' ? 'payment only' : 'invoice and payment'}</td>
                <td style={num}>{money(d.day.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={onConfirm}>Post {list.length} day{list.length === 1 ? '' : 's'}</button>
        </div>
      </div>
    </div>
  );
}

function BatchSummary({ results, running, onClose }) {
  const ok = results.filter((r) => r.ok === true);
  const bad = results.filter((r) => r.ok === false);
  return (
    <div style={{ ...box, padding: 14, margin: '0 0 12px', background: running ? '#eff6ff' : (bad.length ? '#fffbeb' : '#f0fdf4') }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div style={{ fontWeight: 600 }}>
          {running ? `Posting ${Math.min(ok.length + bad.length + 1, results.length)} of ${results.length}...` : `${ok.length} posted, ${bad.length} not posted`}
        </div>
        {!running && <button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
        <tbody>
          {results.map((r) => (
            <tr key={r.date}>
              <td style={{ ...cell, whiteSpace: 'nowrap' }}>{day(r.date)}</td>
              <td style={num}>{money(r.total)}</td>
              <td style={cell}>
                {r.ok === undefined && <span style={small}>{r.active ? 'Posting...' : 'Waiting'}</span>}
                {r.ok === true && <span style={{ color: '#166534' }}>Posted: invoice #{r.invoice || '?'}, payment {r.payment ? `#${r.payment}` : 'in'}</span>}
                {r.ok === false && <span style={{ color: '#991b1b' }}>Not posted: {r.reason}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DryRun({ company }) {
  const [from, setFrom] = useState('2026-09-28');
  const [to, setTo] = useState('2026-09-30');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const run = async () => {
    setBusy(true); setErr(''); setRes(null);
    try { setRes(await authnetQbo('compare', { company, from, to })); } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <div style={{ ...box, padding: 14, marginTop: 18 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Dry run against days already in QuickBooks</div>
      <div style={{ ...small, marginBottom: 8 }}>Builds the entries for days you already entered and lays them beside yours, line by line. Sends nothing to QuickBooks.</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <span style={small}>to</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        <button type="button" className="btn btn-ghost" onClick={run} disabled={busy}>{busy ? 'Checking...' : 'Run dry run'}</button>
      </div>
      {err && <div style={{ color: '#991b1b', fontSize: 13, marginTop: 8 }}>{err}</div>}
      {res && res.days.map((d) => (
        <div key={d.date} style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {day(d.date)}: {d.match ? <span style={{ color: '#166534' }}>matches</span> : <span style={{ color: '#991b1b' }}>does not match</span>}
            {' '}<span style={small}>invoice {d.invoice.inBooks.map((i) => `#${i.docNumber} ${money(i.total)}`).join(', ') || 'none in QuickBooks'}; built {money(d.invoice.built)}</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={head}>Line</th><th style={head}>Account</th><th style={{ ...head, textAlign: 'right' }}>Built</th><th style={{ ...head, textAlign: 'right' }}>In QuickBooks</th><th style={head} /></tr></thead>
            <tbody>
              {d.lines.map((l, i) => (
                <tr key={i}><td style={cell}>{l.card}</td><td style={{ ...cell, ...small }}>{l.builtAccount || l.booksAccount}</td><td style={num}>{money(l.built)}</td><td style={num}>{money(l.inBooks)}</td><td style={cell}>{l.match ? 'same' : 'DIFFERENT'}</td></tr>
              ))}
              <tr>
                <td style={cell}>Payment {day(d.paymentDate, false)}</td><td style={{ ...cell, ...small }}>{d.payment.builtDeposit && (d.payment.builtDeposit.accountName || d.payment.builtDeposit.accountId)}</td>
                <td style={num}>{money(d.payment.built)}</td><td style={num}>{d.payment.inBooks.map((p) => money(p.total)).join(', ')}</td>
                <td style={cell}>{d.payment.inBooks.some((p) => p.total === d.payment.built) ? 'same' : 'DIFFERENT'}</td>
              </tr>
            </tbody>
          </table>
          {d.notes.length > 0 && <div style={{ ...small, marginTop: 4 }}>{d.notes.join(' ')}</div>}
        </div>
      ))}
    </div>
  );
}

export default function AuthnetDeposits() {
  const [status, setStatus] = useState(null);
  const [company, setCompany] = useState('cp');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [days, setDays] = useState([]);
  const [open, setOpen] = useState({});
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [err, setErr] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [posting, setPosting] = useState(false);
  const [postErr, setPostErr] = useState('');
  const [picked, setPicked] = useState({});
  const [batchAsk, setBatchAsk] = useState(null);
  const [batch, setBatch] = useState(null);
  const runId = useRef(0);

  useEffect(() => {
    authnetQbo('status').then((s) => {
      setStatus(s);
      const c = s.companies.cp;
      setFrom(c.defaultFrom); setTo(c.defaultTo);
    }).catch((e) => setErr(e.message));
  }, []);

  const co = status && status.companies[company];

  const patch = (date, p) => setDays((ds) => ds.map((d) => (d.date === date ? { ...d, ...p } : d)));

  const checkOne = useCallback(async (date) => {
    patch(date, { checking: true });
    try {
      const r = await authnetQbo('check', { company, date });
      patch(date, { checking: false, day: r.day, existing: r.existing, stored: r.stored });
    } catch (e) {
      patch(date, { checking: false, checkError: e.message });
    }
  }, [company]);

  const load = useCallback(async () => {
    if (!from || !to) return;
    const id = ++runId.current;
    setLoading(true); setErr(''); setDays([]); setOpen({}); setPicked({});
    try {
      const r = await authnetQbo('list', { company, from, to });
      setDays(r.days);
      setLoading(false);
      const todo = r.days.filter((d) => d.batchIds.length && !(d.inBooks && d.inBooks.length) && !(d.stored && ['posted', 'posting'].includes(d.stored.status)));
      for (let i = 0; i < todo.length; i++) {
        if (runId.current !== id) return;
        setProgress(`Checking ${i + 1} of ${todo.length} days against Authorize.net and QuickBooks`);
        await checkOne(todo[i].date);
      }
      setProgress('');
    } catch (e) { setErr(e.message); setLoading(false); setProgress(''); }
  }, [company, from, to, checkOne]);

  const pickCompany = (k) => {
    runId.current++;
    setCompany(k); setDays([]); setProgress(''); setPicked({}); setBatch(null);
    const c = status && status.companies[k];
    if (c) { setFrom(c.defaultFrom); setTo(c.defaultTo); }
  };

  const openPdf = async (d) => {
    try {
      const r = await authnetQbo('pdf', { company, date: d.date });
      const bin = atob(r.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
    } catch (e) { setErr(e.message); }
  };

  const approve = async () => {
    const d = confirm;
    setPosting(true); setPostErr('');
    try {
      const r = await authnetQbo('approve', { company, date: d.date, expectedTotal: d.day.total });
      patch(d.date, { stored: r.stored, day: r.day || d.day });
      setConfirm(null);
    } catch (e) {
      setPostErr(e.message);
      checkOne(d.date);
    }
    setPosting(false);
  };

  // One day at a time, oldest first, through the same approve action as the single-day
  // button. A failure is recorded and the loop moves on.
  const postBatch = async () => {
    const list = batchAsk;
    setBatchAsk(null);
    const rows = list.map((d) => ({ date: d.date, total: d.day.total }));
    setBatch({ running: true, results: [...rows] });
    for (let i = 0; i < list.length; i++) {
      const d = list[i];
      rows[i] = { ...rows[i], active: true };
      setBatch({ running: true, results: [...rows] });
      try {
        const r = await authnetQbo('approve', { company, date: d.date, expectedTotal: d.day.total });
        const st = r.stored || {};
        patch(d.date, { stored: r.stored, day: r.day || d.day });
        rows[i] = st.status === 'posted'
          ? { date: d.date, total: d.day.total, ok: true, invoice: st.invoiceDocNumber || st.invoiceId, payment: st.paymentId }
          : { date: d.date, total: d.day.total, ok: false, reason: st.error || `left as "${chipFor({ ...d, stored: r.stored }).text}"` };
      } catch (e) {
        rows[i] = { date: d.date, total: d.day.total, ok: false, reason: e.message };
        checkOne(d.date);
      }
      setBatch({ running: i < list.length - 1, results: [...rows] });
    }
    setPicked({});
  };

  const selectable = days.filter((d) => canApprove(d));
  const pickedList = selectable.filter((d) => picked[d.date]).sort((a, b) => (a.date < b.date ? -1 : 1));
  const busyBatch = !!(batch && batch.running);

  const ready = days.filter((d) => canApprove(d) && !(d.stored && d.stored.status === 'partial'));
  const attention = days.filter((d) => d.day && d.day.blockers && d.day.blockers.length && !(d.inBooks && d.inBooks.length));
  const posted = days.filter((d) => d.stored && d.stored.status === 'posted');

  return (
    <div style={{ padding: 16, maxWidth: 1200 }}>
      <h2 style={{ margin: '0 0 4px', color: 'var(--navy, #003b5c)' }}>Authorize.net Deposits</h2>
      <div style={{ ...small, marginBottom: 12, maxWidth: 820 }}>
        Each day's settled card batch, ready to go into QuickBooks the way it is entered by hand: one invoice to Authorize.net with a line per card type
        (sales less refunds), the payment the next day, and the settlement summary attached to both. Nothing is posted until you approve the day.
        Merchant fees stay out; they come from the monthly statement.
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {status && Object.entries(status.companies).map(([k, c]) => (
          <button key={k} type="button" className={k === company ? 'btn btn-primary' : 'btn btn-ghost'} onClick={() => pickCompany(k)}>{c.name}</button>
        ))}
      </div>

      {err && <div style={{ color: '#991b1b', marginBottom: 10 }}>{err}</div>}
      {co && !co.authnet && <div style={{ ...box, padding: 12, marginBottom: 12, background: '#fffbeb' }}>{co.setup}</div>}
      {co && !co.quickbooks && <div style={{ ...box, padding: 12, marginBottom: 12, background: '#fffbeb' }}>{co.name} is not connected to QuickBooks.</div>}

      {co && co.authnet && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <span style={small}>to</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            <button type="button" className="btn btn-primary" onClick={load} disabled={loading}><RefreshCw size={14} /> {loading ? 'Loading...' : 'Show days'}</button>
            {co.lastInvoiceDate && <span style={small}>Last Authorize.net invoice in QuickBooks: {day(co.lastInvoiceDate)}</span>}
          </div>
          {progress && <div style={{ ...small, marginBottom: 8 }}>{progress}</div>}

          {days.length > 0 && (
            <div style={{ display: 'flex', gap: 10, margin: '8px 0 12px', flexWrap: 'wrap' }}>
              <div style={{ ...box, padding: '8px 14px' }}><div style={small}>Ready to approve</div><div style={{ fontSize: 20, fontWeight: 700 }}>{ready.length}</div></div>
              <div style={{ ...box, padding: '8px 14px' }}><div style={small}>Needs attention</div><div style={{ fontSize: 20, fontWeight: 700, color: attention.length ? '#991b1b' : undefined }}>{attention.length}</div></div>
              <div style={{ ...box, padding: '8px 14px' }}><div style={small}>Posted by CPx</div><div style={{ fontSize: 20, fontWeight: 700 }}>{posted.length}</div></div>
            </div>
          )}

          {batch && <BatchSummary results={batch.results} running={batch.running} onClose={() => setBatch(null)} />}

          {selectable.length > 0 && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
              <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  disabled={busyBatch}
                  checked={pickedList.length === selectable.length}
                  onChange={(e) => setPicked(e.target.checked ? Object.fromEntries(selectable.map((d) => [d.date, true])) : {})}
                />
                Select all ready ({selectable.length})
              </label>
              <button type="button" className="btn btn-primary" disabled={!pickedList.length || busyBatch || posting} onClick={() => setBatchAsk(pickedList)}>
                <Check size={14} /> Post selected{pickedList.length ? ` (${pickedList.length})` : ''}
              </button>
              {pickedList.length > 0 && <span style={small}>{money(pickedList.reduce((s, d) => s + Number(d.day.total || 0), 0))}</span>}
            </div>
          )}

          {days.length > 0 && (
            <div style={{ ...box, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={head} />
                    <th style={head} />
                    <th style={head}>Settled</th>
                    {CARD_COLS.map((c) => <th key={c.type} style={{ ...head, textAlign: 'right' }}>{c.label}</th>)}
                    <th style={{ ...head, textAlign: 'right' }}>Invoice and deposit</th>
                    <th style={head}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => {
                    const c = chipFor(d);
                    const cards = (d.day && d.day.cards) || d.cards || [];
                    const isOpen = !!open[d.date];
                    return (
                      <React.Fragment key={d.date}>
                        <tr style={{ cursor: 'pointer' }} onClick={() => setOpen((o) => ({ ...o, [d.date]: !o[d.date] }))}>
                          <td style={{ ...cell, width: 24 }} onClick={(e) => e.stopPropagation()}>
                            {canApprove(d) && (
                              <input
                                type="checkbox"
                                aria-label={`Select ${day(d.date)}`}
                                disabled={busyBatch}
                                checked={!!picked[d.date]}
                                onChange={(e) => setPicked((p) => ({ ...p, [d.date]: e.target.checked }))}
                              />
                            )}
                          </td>
                          <td style={{ ...cell, width: 20 }}>{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                          <td style={{ ...cell, whiteSpace: 'nowrap' }}>{day(d.date)}</td>
                          {CARD_COLS.map((col) => {
                            const x = cards.find((k) => k.type === col.type);
                            return <td key={col.type} style={num}>{x ? money(x.net) : ''}</td>;
                          })}
                          <td style={{ ...num, fontWeight: 600 }}>{d.batchIds.length ? money((d.day && d.day.total) ?? d.total) : ''}</td>
                          <td style={cell}>
                            <Chip c={c} />
                            {d.day && d.day.notes && d.day.notes.some((n) => /^Refunds|voided/.test(n)) && <span style={{ ...small, marginLeft: 6 }}>refunds/voids</span>}
                            {d.checkError && <div style={{ color: '#991b1b', fontSize: 12 }}>{d.checkError}</div>}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr><td colSpan={9} style={{ padding: 0 }}>
                            <DayDetail d={d} onPdf={openPdf} onApprove={(x) => { setPostErr(''); setConfirm(x); }} busy={posting || busyBatch} />
                          </td></tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <DryRun company={company} />
        </>
      )}

      {batchAsk && <BatchConfirm list={batchAsk} onCancel={() => setBatchAsk(null)} onConfirm={postBatch} />}
      {confirm && <ConfirmDialog d={confirm} busy={posting} err={postErr} onCancel={() => setConfirm(null)} onConfirm={approve} />}
    </div>
  );
}
